/**
 * Componente OrderReceipt
 *
 * Renderiza um cupom fiscal estilo impressora térmica (58mm/80mm)
 * com suporte a visualização, download em PDF e impressão.
 * Inclui dados completos do estabelecimento e QR Code para
 * acesso à vitrine pública.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Download, Printer, X, Send } from 'lucide-react';
import QRCode from 'qrcode';
import { Button } from '@components/ui/Button';
import { Modal } from '@components/ui/Modal';
import { useAuth } from '@hooks/useAuth';
import { sendMessage } from '@services/whatsappService';
import { getTenantSettings } from '@services/settingsService';
import { formatCurrency, formatDateTime, formatPhone, formatDocument } from '@utils/formatters';
import { ORDER_TYPE_LABELS, PAYMENT_METHOD_LABELS } from '@utils/constants';
import type { Order } from '@/types';
import type { TenantSettings } from '@/types/settings';
import toast from 'react-hot-toast';
import { escapeHtml as h, safeImageUrl } from '@utils/html';
import { ALCOHOL_DELIVERY_WARNING, orderHasAlcohol } from '@utils/alcohol';

interface OrderReceiptProps {
  order: Order;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Texto do adicional no cupom: "Bacon x2 (R$ 3,00)" — o valor é o total do
 * adicional por unidade do item (preço × quantidade)
 */
function addonLabel(addon: { itemName: string; price: number; quantity?: number }): string {
  const quantity = Math.max(1, Number(addon.quantity) || 1);
  const amount = Number(addon.price) * quantity;
  return `${addon.itemName}${quantity > 1 ? ` x${quantity}` : ''} (${formatCurrency(amount)})`;
}

/**
 * Formata o endereço do estabelecimento em uma única linha
 */
function formatTenantAddress(address: TenantSettings['address']): string {
  if (!address) return '';
  const parts: string[] = [];
  if (address.street) {
    let line = address.street;
    if (address.number) line += `, ${address.number}`;
    if (address.complement) line += ` - ${address.complement}`;
    parts.push(line);
  }
  if (address.neighborhood) parts.push(address.neighborhood);
  const cityState: string[] = [];
  if (address.city) cityState.push(address.city);
  if (address.state) cityState.push(address.state);
  if (cityState.length > 0) parts.push(cityState.join(' - '));
  if (address.zipCode) parts.push(`CEP: ${address.zipCode}`);
  return parts.join('\n');
}

/**
 * Gera o cabeçalho HTML do estabelecimento para o cupom impresso
 */
function generateTenantHeaderHTML(tenant: TenantSettings | null, fallbackName: string): string {
  if (!tenant) {
    return `<div class="center bold" style="font-size:16px;margin-bottom:4px;">${h(fallbackName)}</div>`;
  }

  let html = '';

  const logoUrl = safeImageUrl(tenant.logo);
  if (logoUrl) {
    html += `<div class="center" style="margin-bottom:6px;"><img src="${logoUrl}" alt="${h(tenant.name)}" style="max-width:120px;max-height:60px;margin:0 auto;display:block;" /></div>`;
  }

  html += `<div class="center bold" style="font-size:16px;margin-bottom:4px;">${h(tenant.name)}</div>`;

  if (tenant.document) {
    html += `<div class="center" style="font-size:10px;margin-bottom:2px;">CNPJ: ${h(formatDocument(tenant.document))}</div>`;
  }

  if (tenant.address) {
    const addr = tenant.address;
    if (addr.street) {
      let line = addr.street;
      if (addr.number) line += `, ${addr.number}`;
      if (addr.complement) line += ` - ${addr.complement}`;
      html += `<div class="center" style="font-size:10px;margin-bottom:1px;">${h(line)}</div>`;
    }
    if (addr.neighborhood) {
      html += `<div class="center" style="font-size:10px;margin-bottom:1px;">${h(addr.neighborhood)}</div>`;
    }
    const cityState: string[] = [];
    if (addr.city) cityState.push(addr.city);
    if (addr.state) cityState.push(addr.state);
    if (cityState.length > 0 || addr.zipCode) {
      let line = cityState.join(' - ');
      if (addr.zipCode) line += (line ? ' - ' : '') + `CEP: ${addr.zipCode}`;
      html += `<div class="center" style="font-size:10px;margin-bottom:1px;">${h(line)}</div>`;
    }
  }

  if (tenant.phone) {
    html += `<div class="center" style="font-size:10px;margin-bottom:1px;">Tel: ${h(formatPhone(tenant.phone))}</div>`;
  }

  return html;
}

/**
 * Gera o HTML do cupom fiscal para impressão em impressora térmica
 */
function generateReceiptHTML(order: Order, tenantName: string, tenant?: TenantSettings | null, qrCodeDataUrl?: string): string {
  const itemsHTML = order.items
    .map(
      (item) => `
      <tr>
        <td style="text-align:left;padding:2px 0;">${h(item.quantity)}x ${h(item.productName || item.product?.name)}</td>
        <td style="text-align:right;padding:2px 0;white-space:nowrap;">${h(formatCurrency(item.total))}</td>
      </tr>
      ${
        item.options?.sizeName
          ? `<tr><td colspan="2" style="padding:0 0 2px 12px;font-size:10px;color:#666;">Tamanho: ${h(item.options.sizeName)}</td></tr>`
          : ''
      }
      ${
        item.options?.selectedComplements && item.options.selectedComplements.length > 0
          ? item.options.selectedComplements
              .map(
                (group) =>
                  `<tr><td colspan="2" style="padding:0 0 2px 12px;font-size:10px;color:#666;">${h(group.groupName)}: ${h(group.items.map(i => i.name).join(', '))}${group.items.some(i => i.price > 0) ? ` (+${h(formatCurrency(group.items.reduce((s, i) => s + i.price, 0)))})` : ''}</td></tr>`,
              )
              .join('')
          : ''
      }
      ${
        item.notes
          ? `<tr><td colspan="2" style="padding:0 0 2px 12px;font-size:10px;color:#666;">Obs: ${h(item.notes)}</td></tr>`
          : ''
      }
      ${
        item.addons && item.addons.length > 0
          ? item.addons
              .map(
                (addon) =>
                  `<tr><td colspan="2" style="padding:0 0 2px 12px;font-size:10px;color:#666;">+ ${h(addonLabel(addon))}</td></tr>`,
              )
              .join('')
          : ''
      }`,
    )
    .join('');

  const customerName =
    order.customer?.name || order.customerName || 'Consumidor';
  const customerPhone =
    order.customer?.phone || order.customerPhone || '';

  const schedulingHTML = order.isScheduled && order.scheduledDate
    ? `
      <div style="margin:8px 0;padding:6px;border:2px solid #d97706;background:#fffbeb;">
        <div style="font-weight:bold;font-size:12px;margin-bottom:4px;color:#92400e;">*** PEDIDO AGENDADO ***</div>
        <div style="font-size:11px;font-weight:bold;">
          ${new Date(order.scheduledDate).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          as ${new Date(order.scheduledDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
        </div>
        ${order.schedulingNotes ? `<div style="font-size:10px;margin-top:4px;">Obs: ${h(order.schedulingNotes)}</div>` : ''}
      </div>
      <div style="border-top:1px dashed #000;margin:4px 0;"></div>`
    : '';

  const deliveryAddressHTML =
    order.type === 'DELIVERY' && order.deliveryAddress
      ? `
      <div style="margin:8px 0;">
        <div style="font-weight:bold;font-size:11px;margin-bottom:4px;">ENDERECO DE ENTREGA</div>
        <div style="font-size:11px;">
          ${h(order.deliveryAddress.street)}, ${h(order.deliveryAddress.number)}
          ${order.deliveryAddress.complement ? ` - ${h(order.deliveryAddress.complement)}` : ''}
          <br/>${h(order.deliveryAddress.neighborhood)}
          <br/>${h(order.deliveryAddress.city)} - ${h(order.deliveryAddress.state)}
          <br/>CEP: ${h(order.deliveryAddress.zipCode)}
        </div>
      </div>
      <div style="border-top:1px dashed #000;margin:4px 0;"></div>`
      : '';

  const tenantHeaderHTML = generateTenantHeaderHTML(tenant || null, tenantName);

  const qrCodeHTML = qrCodeDataUrl
    ? `
      <div class="center" style="margin:8px 0;">
        <div style="font-size:10px;font-weight:bold;margin-bottom:4px;">Acompanhe seu pedido:</div>
        <div style="font-size:9px;margin-bottom:4px;color:#666;">Escaneie o QR Code abaixo para acessar<br/>nossa vitrine e acompanhar seu pedido</div>
        <img src="${safeImageUrl(qrCodeDataUrl)}" alt="QR Code" style="width:130px;height:130px;margin:0 auto;display:block;" />
        <div style="font-size:8px;margin-top:4px;color:#999;">${h(window.location.origin)}/loja/${h(tenant?.slug || '')}</div>
      </div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Cupom - Pedido #${h(order.orderNumber || order.number)}</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      line-height: 1.4;
      width: 80mm;
      max-width: 80mm;
      padding: 8px;
      color: #000;
      background: #fff;
    }
    .receipt {
      width: 100%;
    }
    .center {
      text-align: center;
    }
    .bold {
      font-weight: bold;
    }
    .separator {
      border-top: 1px dashed #000;
      margin: 6px 0;
    }
    .double-separator {
      border-top: 2px dashed #000;
      margin: 6px 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
    }
    .total-row td {
      font-weight: bold;
      font-size: 14px;
      padding-top: 4px;
    }
    @media print {
      body {
        width: 80mm;
        max-width: 80mm;
      }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <!-- Cabecalho do Estabelecimento -->
    ${tenantHeaderHTML}
    <div class="separator"></div>
    <div class="center bold" style="font-size:10px;margin:4px 0;letter-spacing:1px;">
      DOCUMENTO SEM VALOR FISCAL
    </div>
    <div class="separator"></div>

    <!-- Dados do pedido -->
    <div class="center bold" style="font-size:14px;margin:4px 0;">
      PEDIDO #${h(order.orderNumber || order.number)}
    </div>
    <div class="center" style="font-size:11px;margin-bottom:4px;">
      ${h(formatDateTime(order.createdAt))}
    </div>
    <div class="center" style="font-size:11px;margin-bottom:4px;">
      Tipo: ${h(ORDER_TYPE_LABELS[order.type] || order.type)}
      ${order.tableNumber ? ` | Mesa: ${h(order.tableNumber)}` : ''}
    </div>
    <div class="separator"></div>

    <!-- Cliente -->
    <div style="margin:4px 0;">
      <div style="font-size:11px;">
        Cliente: ${h(customerName)}
        ${customerPhone ? `<br/>Tel: ${h(formatPhone(customerPhone))}` : ''}
      </div>
    </div>
    <div class="separator"></div>

    <!-- Agendamento -->
    ${schedulingHTML}

    <!-- Endereco de entrega -->
    ${deliveryAddressHTML}

    <!-- Itens -->
    <div class="bold" style="font-size:11px;margin-bottom:4px;">ITENS DO PEDIDO</div>
    <table>
      <tbody>
        ${itemsHTML}
      </tbody>
    </table>

    <div class="double-separator"></div>

    <!-- Totais -->
    <table>
      <tbody>
        <tr>
          <td style="text-align:left;padding:2px 0;">Subtotal</td>
          <td style="text-align:right;padding:2px 0;">${h(formatCurrency(order.subtotal))}</td>
        </tr>
        ${
          order.discount > 0
            ? `<tr>
            <td style="text-align:left;padding:2px 0;">Desconto</td>
            <td style="text-align:right;padding:2px 0;">-${h(formatCurrency(order.discount))}</td>
          </tr>`
            : ''
        }
        ${
          order.deliveryFee > 0
            ? `<tr>
            <td style="text-align:left;padding:2px 0;">Taxa de entrega</td>
            <td style="text-align:right;padding:2px 0;">${h(formatCurrency(order.deliveryFee))}</td>
          </tr>`
            : ''
        }
        ${
          order.serviceFee > 0
            ? `<tr>
            <td style="text-align:left;padding:2px 0;">Taxa de servico</td>
            <td style="text-align:right;padding:2px 0;">${h(formatCurrency(order.serviceFee))}</td>
          </tr>`
            : ''
        }
        <tr class="total-row">
          <td style="text-align:left;padding:4px 0;border-top:1px dashed #000;">TOTAL</td>
          <td style="text-align:right;padding:4px 0;border-top:1px dashed #000;">${h(formatCurrency(order.total))}</td>
        </tr>
      </tbody>
    </table>

    <div class="separator"></div>

    <!-- Pagamento -->
    <div style="margin:4px 0;font-size:11px;">
      Pagamento: ${h(PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod)}
      ${order.changeFor ? `<br/>Troco para: ${h(formatCurrency(order.changeFor))}` : ''}
    </div>

    ${
      order.notes
        ? `<div class="separator"></div>
      <div style="margin:4px 0;font-size:11px;">
        Obs: ${h(order.notes)}
      </div>`
        : ''
    }

    ${
      orderHasAlcohol(order)
        ? `<div class="separator"></div>
      <div style="margin:4px 0;font-size:12px;font-weight:bold;text-align:center;">
        +18 ${h(ALCOHOL_DELIVERY_WARNING)}
      </div>`
        : ''
    }

    <div class="double-separator"></div>

    <!-- QR Code -->
    ${qrCodeHTML}

    <!-- Rodape -->
    <div class="center" style="font-size:11px;margin:8px 0;">
      Obrigado pela preferencia!
    </div>
    <div class="center" style="font-size:10px;color:#666;">
      ${h(tenant?.name || tenantName)} - VendeAi
    </div>
    <div class="separator"></div>
    <div class="center" style="font-size:8px;color:#999;">
      DOCUMENTO SEM VALOR FISCAL
    </div>
  </div>
</body>
</html>`;
}

/**
 * Gera o texto do cupom em formato plain-text para envio via WhatsApp
 */
function generateReceiptText(order: Order, tenantName: string, tenant?: TenantSettings | null): string {
  const sep = '--------------------------------';
  const customerName = order.customer?.name || order.customerName || 'Consumidor';
  const customerPhone = order.customer?.phone || order.customerPhone;

  const name = tenant?.name || tenantName;
  let text = `*${name}*\n`;

  if (tenant?.document) {
    text += `CNPJ: ${formatDocument(tenant.document)}\n`;
  }
  if (tenant?.address) {
    const addr = tenant.address;
    if (addr.street) {
      let line = addr.street;
      if (addr.number) line += `, ${addr.number}`;
      if (addr.complement) line += ` - ${addr.complement}`;
      text += `${line}\n`;
    }
    if (addr.neighborhood) text += `${addr.neighborhood}\n`;
    const cityState: string[] = [];
    if (addr.city) cityState.push(addr.city);
    if (addr.state) cityState.push(addr.state);
    if (cityState.length > 0) text += `${cityState.join(' - ')}\n`;
  }
  if (tenant?.phone) {
    text += `Tel: ${formatPhone(tenant.phone)}\n`;
  }

  text += `${sep}\n`;
  text += `*PEDIDO #${order.orderNumber || order.number}*\n`;
  text += `${formatDateTime(order.createdAt)}\n`;
  text += `Tipo: ${ORDER_TYPE_LABELS[order.type]}`;
  if (order.tableNumber) text += ` | Mesa: ${order.tableNumber}`;
  text += `\n${sep}\n`;
  text += `Cliente: ${customerName}\n`;
  if (customerPhone) text += `Tel: ${formatPhone(customerPhone)}\n`;
  text += `${sep}\n`;

  if (order.isScheduled && order.scheduledDate) {
    const sd = new Date(order.scheduledDate);
    text += `*** PEDIDO AGENDADO ***\n`;
    text += `${sd.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}`;
    text += ` as ${sd.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}\n`;
    if (order.schedulingNotes) text += `Obs: ${order.schedulingNotes}\n`;
    text += `${sep}\n`;
  }

  if (order.type === 'DELIVERY' && order.deliveryAddress) {
    text += `*ENDERECO DE ENTREGA*\n`;
    text += `${order.deliveryAddress.street}, ${order.deliveryAddress.number}`;
    if (order.deliveryAddress.complement) text += ` - ${order.deliveryAddress.complement}`;
    text += `\n${order.deliveryAddress.neighborhood}\n`;
    text += `${order.deliveryAddress.city} - ${order.deliveryAddress.state}\n`;
    text += `CEP: ${order.deliveryAddress.zipCode}\n`;
    text += `${sep}\n`;
  }

  text += `*ITENS DO PEDIDO*\n`;
  for (const item of order.items) {
    text += `${item.quantity}x ${item.productName || item.product?.name} - ${formatCurrency(item.total)}\n`;
    if (item.options?.sizeName) text += `   Tamanho: ${item.options.sizeName}\n`;
    if (item.options?.selectedComplements) {
      for (const group of item.options.selectedComplements) {
        const priceSum = group.items.reduce((s, i) => s + i.price, 0);
        text += `   ${group.groupName}: ${group.items.map(i => i.name).join(', ')}${priceSum > 0 ? ` (+${formatCurrency(priceSum)})` : ''}\n`;
      }
    }
    if (item.notes) text += `   Obs: ${item.notes}\n`;
    // Pedidos antigos podem ter adicionais em formato de objeto: só a lista é impressa
    if (Array.isArray(item.addons)) {
      for (const addon of item.addons) {
        text += `   + ${addonLabel(addon)}\n`;
      }
    }
  }

  text += `${sep}\n`;
  text += `Subtotal: ${formatCurrency(order.subtotal)}\n`;
  if (order.discount > 0) text += `Desconto: -${formatCurrency(order.discount)}\n`;
  if (order.deliveryFee > 0) text += `Taxa de entrega: ${formatCurrency(order.deliveryFee)}\n`;
  if (order.serviceFee > 0) text += `Taxa de servico: ${formatCurrency(order.serviceFee)}\n`;
  text += `*TOTAL: ${formatCurrency(order.total)}*\n`;
  text += `${sep}\n`;
  text += `Pagamento: ${PAYMENT_METHOD_LABELS[order.paymentMethod] || order.paymentMethod}\n`;
  if (order.changeFor) text += `Troco para: ${formatCurrency(order.changeFor)}\n`;
  if (order.notes) text += `\nObs: ${order.notes}\n`;
  if (orderHasAlcohol(order)) text += `\n*+18 ${ALCOHOL_DELIVERY_WARNING}*\n`;
  text += `${sep}\n`;

  if (tenant?.slug) {
    const storeUrl = `${window.location.origin}/loja/${tenant.slug}`;
    text += `Acompanhe seu pedido: ${storeUrl}\n`;
    text += `${sep}\n`;
  }

  text += `Obrigado pela preferencia!\n`;
  text += `${name} - VendeAi\n`;
  text += `${sep}\n`;
  text += `_DOCUMENTO SEM VALOR FISCAL_`;

  return text;
}

// Cache de dados do tenant e QR Code para evitar chamadas repetidas
let tenantCache: { data: TenantSettings; qrCode: string; timestamp: number } | null = null;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

async function generateQRCodeDataUrl(slug: string): Promise<string> {
  const storeUrl = `${window.location.origin}/loja/${slug}`;
  return QRCode.toDataURL(storeUrl, {
    width: 200,
    margin: 1,
    color: { dark: '#000000', light: '#FFFFFF' },
  });
}

/**
 * Componente de visualizacao e impressao do cupom fiscal
 */
export function OrderReceipt({ order, isOpen, onClose }: OrderReceiptProps) {
  const { user } = useAuth();
  const receiptRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [tenant, setTenant] = useState<TenantSettings | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const tenantName = user?.tenantName || 'Estabelecimento';

  /**
   * Busca os dados do estabelecimento e gera o QR Code ao abrir o modal
   */
  const loadTenantData = useCallback(async () => {
    try {
      // Verifica cache (tenant + QR Code)
      if (tenantCache && (Date.now() - tenantCache.timestamp) < CACHE_TTL) {
        setTenant(tenantCache.data);
        setQrCodeDataUrl(tenantCache.qrCode);
        return;
      }

      const tenantData = await getTenantSettings();
      const qrCode = tenantData.slug ? await generateQRCodeDataUrl(tenantData.slug) : '';

      setTenant(tenantData);
      setQrCodeDataUrl(qrCode);
      tenantCache = { data: tenantData, qrCode, timestamp: Date.now() };
    } catch {
      // Falha silenciosa - o cupom continuará funcionando com o fallback do tenantName
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTenantData();
    }
  }, [isOpen, loadTenantData]);

  /**
   * Imprime o cupom abrindo uma nova janela formatada para impressora termica
   */
  const handlePrint = () => {
    setIsPrinting(true);
    const html = generateReceiptHTML(order, tenantName, tenant, qrCodeDataUrl);
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
        setIsPrinting(false);
      };
      // Fallback caso onload nao dispare
      setTimeout(() => setIsPrinting(false), 3000);
    } else {
      setIsPrinting(false);
    }
  };

  /**
   * Faz download do cupom como PDF usando o dialogo de impressao do navegador
   * O usuario deve selecionar "Salvar como PDF" no dialogo de impressao
   */
  const handleDownloadPDF = () => {
    const html = generateReceiptHTML(order, tenantName, tenant, qrCodeDataUrl);
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  };

  /**
   * Envia o cupom para o cliente via WhatsApp
   */
  const handleSendToClient = async () => {
    const phone = order.customer?.phone || order.customerPhone;
    if (!phone) {
      toast.error('Telefone do cliente nao encontrado. Nao e possivel enviar via WhatsApp.');
      return;
    }

    setIsSending(true);
    try {
      const text = generateReceiptText(order, tenantName, tenant);
      await sendMessage({ to: phone, text });
      toast.success('Cupom enviado para o cliente via WhatsApp!');
    } catch {
      toast.error('Erro ao enviar cupom via WhatsApp. Verifique se o WhatsApp esta conectado.');
    } finally {
      setIsSending(false);
    }
  };

  // Atalho de teclado para imprimir
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const customerName =
    order.customer?.name || order.customerName || 'Consumidor';
  const customerPhone = order.customer?.phone || order.customerPhone;

  // Dados do estabelecimento para exibir no preview
  const displayName = tenant?.name || tenantName;
  const displayDocument = tenant?.document ? formatDocument(tenant.document) : null;
  const displayPhone = tenant?.phone ? formatPhone(tenant.phone) : null;
  const displayAddress = tenant?.address ? formatTenantAddress(tenant.address) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cupom do Pedido"
      size="sm"
      zIndex="z-[110]"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button variant="outline" onClick={onClose}>
            <X size={16} />
            Fechar
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            {(order.customer?.phone || order.customerPhone) && (
              <Button variant="outline" onClick={handleSendToClient} isLoading={isSending}>
                <Send size={16} />
                Enviar ao cliente
              </Button>
            )}
            <Button variant="outline" onClick={handleDownloadPDF}>
              <Download size={16} />
              Salvar PDF
            </Button>
            <Button onClick={handlePrint} isLoading={isPrinting}>
              <Printer size={16} />
              Imprimir
            </Button>
          </div>
        </div>
      }
    >
      {/* Preview do cupom */}
      <div className="flex justify-center">
        <div
          ref={receiptRef}
          className="w-[300px] rounded border border-gray-200 bg-white p-4 font-mono text-xs text-gray-900 shadow-inner dark:border-gray-600"
        >
          {/* Logo do Estabelecimento */}
          {tenant?.logo && (
            <div className="mb-1 flex justify-center">
              <img
                src={tenant.logo}
                alt={displayName}
                className="max-h-[40px] max-w-[100px] object-contain"
              />
            </div>
          )}

          {/* Cabecalho do Estabelecimento */}
          <div className="mb-1 text-center text-sm font-bold">
            {displayName}
          </div>
          {displayDocument && (
            <div className="text-center text-[9px] text-gray-500">
              CNPJ: {displayDocument}
            </div>
          )}
          {displayAddress && (
            <div className="mt-0.5 whitespace-pre-line text-center text-[9px] text-gray-500">
              {displayAddress}
            </div>
          )}
          {displayPhone && (
            <div className="mt-0.5 text-center text-[9px] text-gray-500">
              Tel: {displayPhone}
            </div>
          )}
          <div className="my-2 border-t border-dashed border-gray-400" />

          {/* Disclaimer */}
          <div className="mb-1 text-center text-[8px] font-bold tracking-wider text-gray-500 dark:text-gray-400">
            DOCUMENTO SEM VALOR FISCAL
          </div>
          <div className="my-2 border-t border-dashed border-gray-400" />

          {/* Dados do pedido */}
          <div className="mb-1 text-center text-sm font-bold">
            PEDIDO #{order.orderNumber || order.number}
          </div>
          <div className="mb-1 text-center text-[10px] text-gray-500">
            {formatDateTime(order.createdAt)}
          </div>
          <div className="mb-1 text-center text-[10px]">
            Tipo: {ORDER_TYPE_LABELS[order.type]}
            {order.tableNumber ? ` | Mesa: ${order.tableNumber}` : ''}
          </div>
          <div className="my-2 border-t border-dashed border-gray-400" />

          {/* Cliente */}
          <div className="mb-1 text-[10px]">
            <div>Cliente: {customerName}</div>
            {customerPhone && <div>Tel: {formatPhone(customerPhone)}</div>}
          </div>
          <div className="my-2 border-t border-dashed border-gray-400" />

          {/* Agendamento */}
          {order.isScheduled && order.scheduledDate && (
            <>
              <div className="mb-1 rounded border-2 border-amber-500 bg-amber-50 p-2">
                <div className="text-[10px] font-bold text-amber-800">
                  *** PEDIDO AGENDADO ***
                </div>
                <div className="text-[11px] font-bold">
                  {new Date(order.scheduledDate).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                  {' as '}
                  {new Date(order.scheduledDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                {order.schedulingNotes && (
                  <div className="mt-1 text-[9px] text-amber-700">
                    Obs: {order.schedulingNotes}
                  </div>
                )}
              </div>
              <div className="my-2 border-t border-dashed border-gray-400" />
            </>
          )}

          {/* Endereco de entrega */}
          {order.type === 'DELIVERY' && order.deliveryAddress && (
            <>
              <div className="mb-1 text-[10px] font-bold">
                ENDERECO DE ENTREGA
              </div>
              <div className="mb-1 text-[10px]">
                {order.deliveryAddress.street},{' '}
                {order.deliveryAddress.number}
                {order.deliveryAddress.complement &&
                  ` - ${order.deliveryAddress.complement}`}
                <br />
                {order.deliveryAddress.neighborhood}
                <br />
                {order.deliveryAddress.city} -{' '}
                {order.deliveryAddress.state}
                <br />
                CEP: {order.deliveryAddress.zipCode}
              </div>
              <div className="my-2 border-t border-dashed border-gray-400" />
            </>
          )}

          {/* Itens */}
          <div className="mb-1 text-[10px] font-bold">ITENS DO PEDIDO</div>
          <div className="space-y-1">
            {order.items.map((item) => (
              <div key={item.id}>
                <div className="flex justify-between text-[11px]">
                  <span>
                    {item.quantity}x{' '}
                    {item.productName || item.product?.name}
                  </span>
                  <span className="ml-2 shrink-0">
                    {formatCurrency(item.total)}
                  </span>
                </div>
                {item.options?.sizeName && (
                  <div className="pl-3 text-[9px] text-gray-500">
                    Tamanho: {item.options.sizeName}
                  </div>
                )}
                {item.options?.selectedComplements?.map((group, idx) => (
                  <div key={idx} className="pl-3 text-[9px] text-gray-500">
                    {group.groupName}: {group.items.map(i => i.name).join(', ')}
                    {group.items.some(i => i.price > 0) && (
                      <span> (+{formatCurrency(group.items.reduce((s, i) => s + i.price, 0))})</span>
                    )}
                  </div>
                ))}
                {item.notes && (
                  <div className="pl-3 text-[9px] text-gray-500">
                    Obs: {item.notes}
                  </div>
                )}
                {(Array.isArray(item.addons) ? item.addons : []).map((addon, idx) => (
                  <div
                    key={idx}
                    className="pl-3 text-[9px] text-gray-500"
                  >
                    + {addonLabel(addon)}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div className="my-2 border-t-2 border-dashed border-gray-400" />

          {/* Totais */}
          <div className="space-y-0.5 text-[11px]">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between">
                <span>Desconto</span>
                <span>-{formatCurrency(order.discount)}</span>
              </div>
            )}
            {order.deliveryFee > 0 && (
              <div className="flex justify-between">
                <span>Taxa de entrega</span>
                <span>{formatCurrency(order.deliveryFee)}</span>
              </div>
            )}
            {order.serviceFee > 0 && (
              <div className="flex justify-between">
                <span>Taxa de servico</span>
                <span>{formatCurrency(order.serviceFee)}</span>
              </div>
            )}
            <div className="my-1 border-t border-dashed border-gray-400" />
            <div className="flex justify-between text-sm font-bold">
              <span>TOTAL</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
          </div>

          <div className="my-2 border-t border-dashed border-gray-400" />

          {/* Pagamento */}
          <div className="text-[10px]">
            <div>
              Pagamento:{' '}
              {PAYMENT_METHOD_LABELS[order.paymentMethod] ||
                order.paymentMethod}
            </div>
            {order.changeFor && (
              <div>Troco para: {formatCurrency(order.changeFor)}</div>
            )}
          </div>

          {/* Observacoes */}
          {order.notes && (
            <>
              <div className="my-2 border-t border-dashed border-gray-400" />
              <div className="text-[10px]">Obs: {order.notes}</div>
            </>
          )}

          {/* Bebida alcoólica: conferir documento com foto na entrega */}
          {orderHasAlcohol(order) && (
            <>
              <div className="my-2 border-t border-dashed border-gray-400" />
              <div className="text-center text-[11px] font-bold">+18 {ALCOHOL_DELIVERY_WARNING}</div>
            </>
          )}

          <div className="my-2 border-t-2 border-dashed border-gray-400" />

          {/* QR Code */}
          {qrCodeDataUrl && (
            <>
              <div className="my-2 text-center">
                <div className="mb-0.5 text-[9px] font-bold">Acompanhe seu pedido:</div>
                <div className="mb-1 text-[8px] text-gray-500 dark:text-gray-400">
                  Escaneie o QR Code para acessar nossa
                  <br />
                  vitrine e acompanhar seu pedido
                </div>
                <img
                  src={qrCodeDataUrl}
                  alt="QR Code - Acompanhe seu pedido"
                  className="mx-auto"
                  style={{ width: 110, height: 110 }}
                />
                {tenant?.slug && (
                  <div className="mt-1 text-[7px] text-gray-300">
                    {window.location.origin}/loja/{tenant.slug}
                  </div>
                )}
              </div>
              <div className="my-2 border-t border-dashed border-gray-400" />
            </>
          )}

          {/* Rodape */}
          <div className="mt-2 text-center text-[10px]">
            Obrigado pela preferencia!
          </div>
          <div className="mt-1 text-center text-[9px] text-gray-500 dark:text-gray-400">
            {displayName} - VendeAi
          </div>
          <div className="my-1 border-t border-dashed border-gray-400" />
          <div className="text-center text-[7px] text-gray-300">
            DOCUMENTO SEM VALOR FISCAL
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { generateReceiptHTML, generateReceiptText };
export default OrderReceipt;
